import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

const Home = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  // The newsletter form is now rendered persistently at the app level.
  // Removed dynamic formKey; replacing the container was causing the initialized form to be discarded.

  // Function to scroll to newsletter
  const scrollToNewsletter = () => {
    const el = document.getElementById('newsletter');
    if (el) {
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  // No newsletter init here; it's handled by a persistent component to avoid re-initialization issues.

  useEffect(() => {
    const video = videoRef.current;
    
    if (!video) return;

    // Set video properties programmatically for better Safari compatibility
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', ''); // Ensure muted attribute is set
    video.setAttribute('playsinline', ''); // Ensure playsinline attribute is set
    video.defaultMuted = true; // Set defaultMuted for Safari
    video.volume = 0; // Ensure volume is 0

    // Function to attempt playing the video
    const attemptPlay = () => {
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Video autoplay started successfully');
            setIsVideoReady(true);
          })
          .catch((error) => {
            console.log('Autoplay prevented, will retry on user interaction:', error.message);
          });
      }
    };

    // Try to play when metadata is loaded
    const handleLoadedMetadata = () => {
      attemptPlay();
    };

    // Try to play when video can play
    const handleCanPlay = () => {
      if (!isVideoReady) {
        attemptPlay();
      }
    };

    // Attach event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);

    // Try to load the video
    video.load();

    // Try playing immediately
    attemptPlay();

    // Retry on any user interaction (Safari workaround)
    const handleUserInteraction = () => {
      if (video.paused) {
        attemptPlay();
      }
    };

    // Add multiple event listeners for better detection
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    document.addEventListener('scroll', handleUserInteraction, { once: true });
    document.addEventListener('wheel', handleUserInteraction, { once: true, passive: true });
    document.addEventListener('mousemove', handleUserInteraction, { once: true });
    document.addEventListener('touchmove', handleUserInteraction, { once: true, passive: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('scroll', handleUserInteraction);
      document.removeEventListener('wheel', handleUserInteraction);
      document.removeEventListener('mousemove', handleUserInteraction);
      document.removeEventListener('touchmove', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [isVideoReady]);

  return (
    <div className="home-container">
      {/* Floating Newsletter Button */}
      <button 
        className="floating-newsletter-btn" 
        onClick={scrollToNewsletter}
        aria-label="Subscribe to Newsletter"
      >
        <span className="btn-icon">📧</span>
        <span className="btn-text">Sign up for<br />our Newsletter</span>
      </button>

      {/* Hero Section */}
      <div className="hero-section">
        <video 
          ref={videoRef}
          className="hero-video-background" 
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          x-webkit-airplay="deny"
        >
          <source src="https://pub-6bf81cd64e2748a18203ba2fdd7b67aa.r2.dev/background.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Transparent overlay to hide Safari play button */}
        <div className="video-overlay-blocker"></div>
        <div className="hero-overlay">
          <div className="hero-logo-container">
            <img src="/images/aarings.png" alt="Abstinent Adolescents Rings" className="hero-logo-rings" />
            <img src="/images/aatxt.png" alt="Abstinent Adolescents Text" className="hero-logo-text" />
          </div>
          <p className="hero-subtitle">Empowering youth to consider and choose abstinence for teen holistic health and beyond.</p>
          <button onClick={() => navigate('/about-us')} className="hero-button">
            Learn More About Us
          </button>
        </div>
      </div>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="mission-content">
          <h2>Our Mission</h2>
          <p>
           To glorify God by providing community for members, providing others with sexual abstinence education, providing mentorship for seeking individuals, and emphasizing the importance of marriage and holistic health.
          </p>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="pillars-section">
        <h2>Our Three Pillars</h2>
        <div className="pillars-grid">
          <div className="pillar-card">
            <div className="pillar-icon">👥</div>
            <h3>Community</h3>
            <p>It can be easy to feel like you’re walking alone on the abstinence journey. We’ve established a strong community of students committed to abstinence. We seek to provide a community for people seeking other individuals to walk alongside them in their journey.</p>
          </div>
          <div className="pillar-card">
            <div className="pillar-icon">📚</div>
            <h3>Education</h3>
            <p>Sex education in schools has often neglected abstinence as a valid choice. We want to change that by informing people about the physical, mental, and spiritual health benefits of abstaining. We do this by hosting educational events that provide information on abstinence and sex education research.</p>
          </div>
          <div className="pillar-card">
            <div className="pillar-icon">🌱</div>
            <h3>Mentorship</h3>
            <p>We provide mentorship for those who need help throughout their abstinence journey. This is a 1:1 mentor/mentee system dedicated to helping people who would benefit from having someone who can provide guidance, encouragement, and direction to them. This program has been established virtually.</p>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <h2>Join Our Movement</h2>
        <p>Be part of a generation making positive choices</p>
        <div className="cta-buttons">
          <button onClick={() => navigate('/our-story')} className="cta-button primary">
            Our Story
          </button>
          <button onClick={() => navigate('/what-we-believe')} className="cta-button secondary">
            What We Believe
          </button>
        </div>
      </section>

      {/* Newsletter Section moved to persistent component to keep it mounted across routes */}
    </div>
  );
};

export default Home;

